import { useContext, useMemo, useState } from "react";
import { Card, Collapse, Icon, MenuItem, Select, Tooltip, Modal, Fade } from "@mui/material";
import { Fragment } from "react";
import dayjs from "dayjs";
import SoftBox from "components/SoftBox";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import Loading from "components/Carteles/Loading";
import remitoPDF from "assets/images/Generated Image September 24, 2025 - 11_05AM.png";
import pdfIcon from "assets/images/file-type-pdf2.svg";
import { MenuContext } from "context/menuContext";

const mappings = {
	vencida: { color: "#f44336", icon: "error", label: "Vencida" },
	anulada: { color: "#212121", icon: "block", label: "Anulada" },
	pendiente: { color: "#ff9800", icon: "schedule", label: "Pendiente" },
	entregada: { color: "#4caf50", icon: "check_circle", label: "Entregada" },
	"entrega parcial": { color: "#2196f3", icon: "radio_button_checked", label: "Entrega parcial" },
};

const CALENDAR_MONTHS = ["Ene.", "Feb.", "Mar.", "Abr.", "May.", "Jun.", "Jul.", "Ago.", "Sept.", "Oct.", "Nov.", "Dic."];

const CALENDAR_DAYS = ["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"];

const getProveedorName = (proveedor) => {
	if (!proveedor) return "";
	return [proveedor.razonSocial || proveedor.nombre, proveedor.apellido, proveedor.documento ? `- ${proveedor.documento}` : null].filter(Boolean).join(" ");
};

const getProvisionStatusMeta = (estadoRaw) => {
	const estado = (estadoRaw || "").toLowerCase();

	if (mappings[estado]) {
		return mappings[estado];
	}

	if (estado.includes("parcial")) {
		return mappings["entrega parcial"];
	}

	return { color: "#9e9e9e", icon: "help_outline", label: estadoRaw || "Sin estado" };
};

const formatHourValue = (time) => {
	if (!time) return null;
	const [hours, minutes] = time.split(":").slice(0, 2);
	return `${hours}:${minutes}`;
};

const getScheduleData = (startTime, endTime) => {
	const startHour = formatHourValue(startTime);
	const endHour = formatHourValue(endTime);

	let label = "-";
	if (startHour && endHour) {
		label = startHour === endHour ? `A las ${startHour}` : `Entre las ${startHour} y ${endHour}hs`;
	} else if (startHour || endHour) {
		const hour = startHour || endHour;
		label = `A las ${hour}`;
	}

	const tooltip = (() => {
		if (startHour && endHour) {
			return `Preferentemente entre las ${startHour} y ${endHour}hs`;
		}

		if (startHour || endHour) {
			const hour = startHour || endHour;
			return `Preferentemente a las ${hour}hs`;
		}

		return "Sin horario especificado";
	})();

	return { startHour, endHour, label, tooltip };
};

const getDetalleUnidad = (detalle) => {
	const unidadFuente = detalle?.materiaPrima?.tipoUnidad || detalle?.materiaPrimaRecepcion?.tipoUnidad || detalle?.pedidoCompraDetalle?.tipoUnidad || null;

	if (!unidadFuente) {
		return "-";
	}

	return unidadFuente.abreviatura || unidadFuente.nombre || "-";
};

const getDetalleNombre = (detalle) => {
	return detalle?.materiaPrima?.nombre || detalle?.materiaPrimaRecepcion?.nombre || detalle?.pedidoCompraDetalle?.nombre || "-";
};

const mapProvisionDetalleToSolicitudItem = (detalle) => {
	if (!detalle) {
		return null;
	}

	return {
		idMateriaPrima: detalle?.materiaPrima?.id ?? detalle?.materiaPrimaRecepcion?.id ?? null,
		idProvisionDetalle: detalle.id ?? null,
		nombre: getDetalleNombre(detalle),
		cantidad: detalle.cantidad ?? 0,
		unidad: getDetalleUnidad(detalle),
	};
};

const buildSolicitudFromProvision = (provision) => {
	if (!provision) {
		return null;
	}

	const detalles = (provision.detalles || []).map(mapProvisionDetalleToSolicitudItem).filter(Boolean);

	return {
		id: provision.id ? `provision-${provision.id}` : "provision-sin-id",
		fechaEntrega: provision.fechaVencimiento || null,
		horaEntregaInicio: null,
		horaEntregaFin: null,
		observaciones: null,
		detalles,
		proveedor: provision.proveedor,
		provision,
	};
};

const selectLatestSolicitudFromProvision = (provision) => {
	const solicitudes = provision?.solicitudesEntregaProveedor;
	if (!Array.isArray(solicitudes) || !solicitudes.length) {
		return null;
	}

	const sorted = [...solicitudes].sort((a, b) => {
		const dateA = dayjs(a?.fechaEntrega || a?.createdAt);
		const dateB = dayjs(b?.fechaEntrega || b?.createdAt);

		if (!dateA.isValid() && !dateB.isValid()) return 0;
		if (!dateA.isValid()) return 1;
		if (!dateB.isValid()) return -1;

		return dateB.valueOf() - dateA.valueOf();
	});

	const latest = sorted[0];
	if (!latest) {
		return null;
	}

	return {
		...latest,
		provision,
	};
};

const formatNumberValue = (value) => {
	if (value === null || value === undefined || Number.isNaN(Number(value))) {
		return "-";
	}

	return Number(value).toLocaleString("es-AR", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	});
};

const formatProvisionDeadlineDate = (dueDate, today) => {
	if (!dueDate || !dueDate.isValid()) {
		return "-";
	}

	const baseMonth = CALENDAR_MONTHS[dueDate.month()] || dueDate.format("MMMM");
	const monthName = baseMonth ? baseMonth.toLowerCase() : "";
	const sameYear = dueDate.year() === today.year();

	return sameYear ? `${dueDate.date()} de ${monthName}` : `${dueDate.date()} de ${monthName} de ${dueDate.year()}`;
};

const getProvisionDeadlineInfo = (dueDate, statusLabel, today) => {
	if (!dueDate || !dueDate.isValid()) {
		return { message: "Fecha de vencimiento no disponible", color: "text" };
	}

	const dueDateStart = dueDate.startOf("day");
	const diff = dueDateStart.diff(today, "day");
	const longDate = formatProvisionDeadlineDate(dueDate, today);
	const statusLower = (statusLabel || "").toLowerCase();
	const isOverdue = statusLower === "vencida" || diff < 0;

	if (isOverdue) {
		const overdueDiff = diff < 0 ? Math.abs(diff) : today.diff(dueDateStart, "day");
		const overdueDays = overdueDiff > 0 ? overdueDiff : 0;

		if (overdueDays === 0) {
			return { message: `Vencio hoy (${longDate})`, color: "error" };
		}

		const label = overdueDays === 1 ? "dia" : "dias";
		return {
			message: `Vencida hace ${overdueDays} ${label}, vencio el ${longDate}`,
			color: "error",
		};
	}

	if (diff === 0) {
		return { message: `Hoy vence, tienes tiempo hasta el ${longDate}`, color: "error" };
	}

	if (diff === 1) {
		return { message: `Falta 1 dia, tienes tiempo hasta el ${longDate}`, color: "warning" };
	}

	return { message: `Faltan ${diff} dias, tienes tiempo hasta el ${longDate}`, color: "text" };
};
function CalendarioEntregas({
	idProveedor = null,
	solicitudes = [],
	loadingSolicitudes = false,
	provisiones = [],
	loadingProvisiones = false,
	onOpenRemitos,
	onGeneratePdf,
	forceOpenCollapse = false,
}) {
	const [calendarOpen, setCalendarOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState(null);
	const [calendarMonth, setCalendarMonth] = useState(dayjs().month());
	const [calendarYear, setCalendarYear] = useState(dayjs().year());
	const [provisionMonthFilter, setProvisionMonthFilter] = useState(dayjs().month());
	const [provisionYearFilter, setProvisionYearFilter] = useState(dayjs().year());
	const [provisionPage, setProvisionPage] = useState(1);
	const [provisionLimit] = useState(10);
	const [expandActive, setExpandActive] = useState(false);
	const [OPoption, setOPoption] = useState(["Pendiente", "Entrega parcial"]);
	const [detailsModal, setDetailsModal] = useState({ open: false, solicitud: null });
	const { menu } = useContext(MenuContext);

	const today = dayjs().startOf("day");
	const normalizedProveedorId = idProveedor !== null && idProveedor !== undefined ? String(idProveedor) : null;

	const canOpenRemitos = typeof onOpenRemitos === "function";

	const solicitudesByProveedor = useMemo(() => {
		if (!Array.isArray(solicitudes)) return [];
		if (normalizedProveedorId === null) return solicitudes;
		return solicitudes.filter((solicitud) => {
			const solicitudProveedorId = solicitud?.proveedor?.id ?? solicitud?.provision?.proveedor?.id ?? null;
			return solicitudProveedorId !== null && String(solicitudProveedorId) === normalizedProveedorId;
		});
	}, [solicitudes, normalizedProveedorId]);

	const solicitudByProvisionId = useMemo(() => {
		const map = new Map();
		(solicitudesByProveedor || []).forEach((solicitud) => {
			const provisionId = solicitud?.provision?.id;
			if (provisionId === null || provisionId === undefined) {
				return;
			}

			const key = String(provisionId);
			const current = map.get(key);
			if (!current) {
				map.set(key, solicitud);
				return;
			}

			const currentDate = dayjs(current?.fechaEntrega || current?.createdAt);
			const candidateDate = dayjs(solicitud?.fechaEntrega || solicitud?.createdAt);

			if (!currentDate.isValid() && candidateDate.isValid()) {
				map.set(key, solicitud);
				return;
			}

			if (candidateDate.isValid() && currentDate.isValid() && candidateDate.isAfter(currentDate)) {
				map.set(key, solicitud);
			}
		});
		return map;
	}, [solicitudesByProveedor]);

	const provisionesByProveedor = useMemo(() => {
		if (!Array.isArray(provisiones)) return [];
		if (normalizedProveedorId === null) return provisiones;
		return provisiones.filter((provision) => {
			const provisionProveedorId = provision?.proveedor?.id ?? null;
			return provisionProveedorId !== null && String(provisionProveedorId) === normalizedProveedorId;
		});
	}, [provisiones, normalizedProveedorId]);

	const calendarEvents = useMemo(() => {
		return (solicitudesByProveedor || [])
			.filter((item) => item.fechaEntrega)
			.map((item) => {
				const date = dayjs(item.fechaEntrega).startOf("day");
				const schedule = getScheduleData(item.horaEntregaInicio, item.horaEntregaFin);
				const statusMeta = getProvisionStatusMeta(item.provision?.estado);

				return {
					id: item.id,
					date,
					dateKey: date.format("YYYY-MM-DD"),
					proveedorNombre: getProveedorName(item.proveedor),
					statusMeta,
					provisionId: item.provision?.id || null,
					pedidoId: item.provision?.pedidoCompra?.id || null,
					provision: item.provision,
					pedidoNombre: item.provision?.pedidoCompra?.nombre || "-",
					schedule,
					solicitud: item,
				};
			});
	}, [solicitudesByProveedor]);

	const eventsByDate = useMemo(() => {
		const map = new Map();
		calendarEvents.forEach((event) => {
			if (!map.has(event.dateKey)) {
				map.set(event.dateKey, []);
			}
			map.get(event.dateKey).push(event);
		});
		return map;
	}, [calendarEvents]);

	const selectedDateKey = selectedDate ? selectedDate.format("YYYY-MM-DD") : null;

	const selectedDayEvents = useMemo(() => {
		if (!selectedDateKey) return [];
		const events = eventsByDate.get(selectedDateKey) || [];
		return [...events].sort((a, b) => {
			const aHour = a.schedule.startHour || a.schedule.endHour || "";
			const bHour = b.schedule.startHour || b.schedule.endHour || "";
			if (aHour && bHour && aHour !== bHour) {
				return aHour.localeCompare(bHour);
			}
			if (aHour) return -1;
			if (bHour) return 1;
			return (a.proveedorNombre || "").localeCompare(b.proveedorNombre || "", "es", {
				sensitivity: "base",
			});
		});
	}, [eventsByDate, selectedDateKey]);

	const yearOptions = useMemo(() => {
		const baseYear = dayjs().year();
		const years = Array.from({ length: 11 }, (_, index) => baseYear - 5 + index);
		if (!years.includes(calendarYear)) {
			years.push(calendarYear);
			years.sort((a, b) => a - b);
		}
		return years;
	}, [calendarYear]);

	const provisionYearOptions = useMemo(() => {
		const baseYear = dayjs().year();
		const years = Array.from({ length: 11 }, (_, index) => baseYear - 5 + index);
		if (!years.includes(provisionYearFilter)) {
			years.push(provisionYearFilter);
			years.sort((a, b) => a - b);
		}
		return years;
	}, [provisionYearFilter]);

	const calendarDays = useMemo(() => {
		const monthStart = dayjs().year(calendarYear).month(calendarMonth).startOf("month");
		const firstDayOfMonth = monthStart.day();
		const daysInMonth = monthStart.daysInMonth();
		const prevMonth = monthStart.subtract(1, "month");
		const nextMonth = monthStart.add(1, "month");

		const days = [];

		for (let i = firstDayOfMonth - 1; i >= 0; i -= 1) {
			const dayNumber = prevMonth.daysInMonth() - i;
			const date = prevMonth.date(dayNumber);
			days.push({ date, current: false });
		}

		for (let day = 1; day <= daysInMonth; day += 1) {
			days.push({ date: monthStart.date(day), current: true });
		}

		const remainingCells = (7 - (days.length % 7)) % 7;
		for (let i = 1; i <= remainingCells; i += 1) {
			days.push({ date: nextMonth.date(i), current: false });
		}

		return days;
	}, [calendarMonth, calendarYear]);

	const formattedSelectedDate = selectedDate
		? `${CALENDAR_DAYS[selectedDate.day()]}, ${selectedDate.date()} de ${CALENDAR_MONTHS[selectedDate.month()]} de ${selectedDate.year()}`
		: "Selecciona un dia";

	const handleToggleCalendar = () => {
		setCalendarOpen((prev) => !prev);
	};

	const handleCalendarMonthChange = (event) => {
		setCalendarMonth(Number(event.target.value));
		setSelectedDate(null);
	};

	const handleCalendarYearChange = (event) => {
		setCalendarYear(Number(event.target.value));
		setSelectedDate(null);
	};

	const handleProvisionMonthChange = (event) => {
		setProvisionMonthFilter(Number(event.target.value));
		setProvisionPage(1);
	};

	const handleProvisionYearChange = (event) => {
		setProvisionYearFilter(Number(event.target.value));
		setProvisionPage(1);
	};

	const handleDaySelection = (date) => {
		setSelectedDate(date);
	};

	const handleOpenDetails = (solicitud) => {
		if (!solicitud) {
			return;
		}

		setDetailsModal({ open: true, solicitud });
	};

	const handleCloseDetails = () => {
		setDetailsModal({ open: false, solicitud: null });
	};

	const goToPreviousProvisionPage = () => {
		setProvisionPage((prev) => Math.max(prev - 1, 1));
	};

	const goToNextProvisionPage = () => {
		if (provisionesByProveedor.length < provisionLimit) {
			return;
		}

		setProvisionPage((prev) => prev + 1);
	};

	const canGoPreviousProvisionPage = provisionPage > 1;
	const canGoNextProvisionPage = provisionesByProveedor.length > provisionLimit;

	const filteredProvisiones = useMemo(() => {
		return provisionesByProveedor
			.filter((provision) => {
				const tipoCompra = provision?.pedidoCompra?.tipoCompra;
				const requiereDemanda = provision?.pedidoCompra?.pliegoRequiereDemanda;
				const matchesTipoCompra = tipoCompra === "Directa" || (tipoCompra === "Concurso" && requiereDemanda === "NO");
				if (!matchesTipoCompra) {
					return false;
				}

				if (!provision?.fechaVencimiento) {
					return false;
				}

				const dueDate = dayjs(provision.fechaVencimiento);
				if (!dueDate.isValid()) {
					return false;
				}

				return dueDate.month() === provisionMonthFilter + 1 && dueDate.year() === provisionYearFilter && OPoption.includes(provision.estado);
			})
			.sort((a, b) => {
				const dateA = dayjs(a.fechaVencimiento);
				const dateB = dayjs(b.fechaVencimiento);
				if (!dateA.isValid() && !dateB.isValid()) return 0;
				if (!dateA.isValid()) return 1;
				if (!dateB.isValid()) return -1;
				return dateA.valueOf() - dateB.valueOf();
			});
	}, [provisionesByProveedor, provisionMonthFilter, provisionYearFilter, OPoption]);

	const detalleSolicitud = detailsModal.solicitud;

	const detalleRows = useMemo(() => {
		if (!detailsModal.solicitud) {
			return [];
		}

		const solicitud = detailsModal.solicitud;
		const provisionDetails = solicitud.provision?.detalles || [];
		const provisionMap = new Map(provisionDetails.map((detalle) => [String(detalle.id), detalle]));

		return (solicitud.detalles || []).map((detalle) => {
			const provDetalle = provisionMap.get(String(detalle.idProvisionDetalle)) || {};
			const totalProvision = provDetalle?.cantidad ?? 0;
			const cantidadEntregada = provDetalle?.cantidadEntregada ?? 0;
			const faltante = Math.max(totalProvision - cantidadEntregada, 0);

			return {
				nombre: detalle.nombre || "-",
				cantidadSolicitada: detalle.cantidad ?? 0,
				unidad: detalle.unidad || "-",
				totalProvision,
				cantidadEntregada,
				faltante,
				completa: faltante === 0,
			};
		});
	}, [detailsModal]);

	return (
		<>
			<Card sx={{ mb: 2 }}>
				<SoftBox
					component="button"
					type="button"
					display="flex"
					justifyContent="space-between"
					alignItems="center"
					p={3}
					aria-expanded={forceOpenCollapse || calendarOpen}
					aria-controls="calendario-entregas-contenido"
					sx={{
						width: "100%",
						border: 0,
						background: "transparent",
						font: "inherit",
						cursor: forceOpenCollapse ? "default" : "pointer",
						"&:hover": forceOpenCollapse ? {} : { backgroundColor: "#fffafd" },
						"&:focus-visible": {
							outline: "3px solid rgba(240, 5, 122, 0.35)",
							outlineOffset: -3,
						},
					}}
					onClick={forceOpenCollapse ? () => { } : handleToggleCalendar}
				>
					<SoftTypography variant="h6">Calendario de entregas</SoftTypography>
					<Icon>{forceOpenCollapse || calendarOpen ? "expand_less" : "expand_more"}</Icon>
				</SoftBox>
				<Collapse in={forceOpenCollapse || calendarOpen} timeout="auto" unmountOnExit>
					<SoftBox id="calendario-entregas-contenido" sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
						{loadingSolicitudes ? (
							<Loading />
						) : (
							<SoftBox display="grid" gridTemplateColumns={{ sm: "1fr", lg: "7fr 3fr" }} gap={1}>
								<Card
									sx={{
										p: { xs: 1.5, sm: 2 },
										boxShadow: "none",
										border: "1px solid #e0e0e0",
										borderRadius: 2,
									}}
								>
									<SoftBox display="flex" flexDirection="column" gap={3}>
										<SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<Icon color="primary">calendar_today</Icon>
												<SoftTypography component="span" variant="h6" fontWeight="bold">
													Calendario
												</SoftTypography>
											</SoftBox>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<Select value={calendarMonth} onChange={handleCalendarMonthChange} size="small">
													{CALENDAR_MONTHS.map((monthName, index) => (
														<MenuItem key={monthName} value={index}>
															{monthName}
														</MenuItem>
													))}
												</Select>
												<Select value={calendarYear} onChange={handleCalendarYearChange} size="small">
													{yearOptions.map((yearOption) => (
														<MenuItem key={yearOption} value={yearOption}>
															{yearOption}
														</MenuItem>
													))}
												</Select>
											</SoftBox>
										</SoftBox>
										<SoftBox minWidth={0}>
											<SoftBox display="grid" gridTemplateColumns="repeat(7, minmax(0, 1fr))" gap={{ xs: 0.25, sm: 1 }}>
												{CALENDAR_DAYS.map((day) => (
													<SoftBox
														key={day}
														display="flex"
														justifyContent="center"
														py={0.5}
														sx={{
															borderRadius: 1,
															backgroundColor: "#fde8cc",
														}}
													>
														<SoftTypography
															variant="caption"
															fontWeight="bold"
															sx={{ color: "#713d00", fontSize: { xs: "0.625rem", sm: "0.7rem" } }}
														>
															<SoftBox component="span" display={{ xs: "inline", sm: "none" }}>
																{day.slice(0, 1)}
															</SoftBox>
															<SoftBox component="span" display={{ xs: "none", sm: "inline" }}>
																{day}
															</SoftBox>
														</SoftTypography>
													</SoftBox>
												))}
											</SoftBox>
											<SoftBox
												mt={2}
												display="grid"
												gridTemplateColumns="repeat(7, minmax(0, 1fr))"
												columnGap={{ xs: 0.25, sm: 0.5 }}
												rowGap={{ xs: 1, sm: 1.5 }}
											>
												{calendarDays.map(({ date, current }, index) => {
													const dateKey = date.format("YYYY-MM-DD");
													const hasEvents = eventsByDate.has(dateKey);
													const isSelected = selectedDate && selectedDate.isSame(date, "day");
													return (
														<SoftBox key={`${dateKey}-${index}`} display="flex" justifyContent="center" alignItems="center">
															<SoftBox
																component={current ? "button" : "span"}
																type={current ? "button" : undefined}
																onClick={current ? () => handleDaySelection(date) : undefined}
																aria-label={current ? `Seleccionar ${date.format("DD/MM/YYYY")}` : undefined}
																aria-pressed={current ? Boolean(isSelected) : undefined}
																sx={{
																	position: "relative",
																	width: { xs: 30, sm: 44 },
																	height: { xs: 30, sm: 44 },
																	border: 0,
																	p: 0,
																	borderRadius: "50%",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	cursor: current ? "pointer" : "default",
																	backgroundColor: isSelected ? "#26c6da" : "transparent",
																	color: !current ? "#b0bec5" : isSelected ? "#ffffff" : "#263238",
																	transition: "all 0.2s ease-in-out",
																	"&:hover": current ? { backgroundColor: isSelected ? "#26c6da" : "#e0f7fa" } : {},
																	"&:focus-visible": current
																		? {
																			outline: "3px solid rgba(240, 5, 122, 0.35)",
																			outlineOffset: 2,
																		}
																		: {},
																}}
															>
																<SoftTypography variant="body2" fontWeight="bold">
																	{date.date()}
																</SoftTypography>
																{hasEvents && (
																	<SoftBox
																		sx={{
																			width: "15px",
																			height: "15px",
																			borderRadius: "100%",
																			position: "absolute",
																			zIndex: 2,
																			top: "0",
																			right: "0",
																			fontSize: "15px",
																		}}
																	>
																		<Icon sx={{ fontSize: 10, color: "#e91e63" }}>fiber_manual_record</Icon>
																	</SoftBox>
																)}
															</SoftBox>
														</SoftBox>
													);
												})}
											</SoftBox>
										</SoftBox>
										<SoftBox>
											<SoftTypography variant="button" fontWeight="bold">
												{formattedSelectedDate}
											</SoftTypography>
											{calendarEvents.length === 0 ? (
												<SoftBox mt={4} display="flex" alignItems="center" justifyContent="center">
													<SoftTypography component="span" variant="body2" color="text">
														No hay solicitudes de entrega registradas
													</SoftTypography>
												</SoftBox>
											) : selectedDate ? (
												selectedDayEvents.length ? (
													<SoftBox mt={2}>
														<SoftBox
															display={{ xs: "none", lg: "grid" }}
															gridTemplateColumns="repeat(5, 1fr)"
															px={2}
															py={1}
															sx={{ backgroundColor: "#f0f4f8", borderRadius: 1 }}
														>
															<SoftTypography variant="caption" fontWeight="bold">
																Estado
															</SoftTypography>
															<SoftTypography variant="caption" fontWeight="bold">
																OP
															</SoftTypography>
															<SoftTypography variant="caption" fontWeight="bold">
																Pedido
															</SoftTypography>
															<SoftTypography variant="caption" fontWeight="bold" textAlign="center">
																Horario
															</SoftTypography>
															<SoftTypography variant="caption" fontWeight="bold" textAlign="center">
																Acciones
															</SoftTypography>
														</SoftBox>

														{selectedDayEvents.map((event) => (
															<SoftBox key={event.id}>
																<SoftBox
																	display={{ xs: "none", lg: "grid" }}
																	gridTemplateColumns="repeat(5, 1fr)"
																	px={2}
																	py={1.5}
																	sx={{ borderBottom: "1px solid #eceff1", alignItems: "center" }}
																>
																	<SoftBox display="flex" alignItems="center" gap={1}>
																		<Icon sx={{ color: event.statusMeta.color }}>{event.statusMeta.icon}</Icon>
																		<SoftTypography variant="caption">{event.statusMeta.label}</SoftTypography>
																	</SoftBox>
																	<SoftTypography variant="caption">{event.provisionId || "-"}</SoftTypography>
																	<SoftTypography variant="caption">
																		{event.pedidoId ? `# ${event.pedidoId} ${event.pedidoNombre}` : "-"}
																	</SoftTypography>
																	<SoftTypography variant="caption" textAlign="center" title={event.schedule.tooltip}>
																		{event.schedule.label}
																	</SoftTypography>
																	<SoftBox display="flex" justifyContent="center" gap={1}>
																		<SoftButton
																			variant="text"
																			color="dark"
																			size="small"
																			iconOnly
																			circular
																			onClick={() => onGeneratePdf?.(event.solicitud)}
																			title="PDF OP"
																		>
																			<SoftBox
																				component="img"
																				src={pdfIcon}
																				alt="PDF OP"
																				sx={{ width: 24, height: 24 }}
																			/>
																		</SoftButton>
																		<SoftButton
																			variant="text"
																			color="info"
																			size="small"
																			iconOnly
																			circular
																			onClick={() => handleOpenDetails(event.solicitud)}
																			title="Ver productos"
																		>
																			<Icon sx={{ fontSize: 18 }}>visibility</Icon>
																		</SoftButton>
																		{canOpenRemitos && (
																			<SoftButton
																				variant="text"
																				color="dark"
																				size="small"
																				iconOnly
																				circular
																				onClick={() => onOpenRemitos?.(event.provision)}
																				title="Ver remitos"
																			>
																				<SoftBox
																					component="img"
																					src={remitoPDF}
																					alt="Ver remitos"
																					sx={{ width: 24, height: 24 }}
																				/>
																			</SoftButton>
																		)}
																	</SoftBox>
																</SoftBox>

																<SoftBox
																	display={{ xs: "block", lg: "none" }}
																	sx={{
																		border: "1px solid #eceff1",
																		borderRadius: 1,
																		mb: 2,
																		p: 2,
																		backgroundColor: "#fafafa",
																	}}
																>
																	<SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
																		<SoftBox display="flex" alignItems="center" gap={1}>
																			<Icon sx={{ color: event.statusMeta.color }}>{event.statusMeta.icon}</Icon>
																			<SoftTypography variant="body2" fontWeight="bold">
																				{event.statusMeta.label}
																			</SoftTypography>
																		</SoftBox>
																		<SoftBox display="flex" justifyContent="center" gap={1}>
																			<SoftButton
																				variant="text"
																				color="dark"
																				size="small"
																				iconOnly
																				circular
																				onClick={() => onGeneratePdf?.(event.solicitud)}
																				title="Generar PDF"
																			>
																				<SoftBox
																					component="img"
																					src={pdfIcon}
																					alt="Generar PDF"
																					sx={{ width: 20, height: 20 }}
																				/>
																			</SoftButton>
																			<SoftButton
																				variant="text"
																				color="info"
																				size="small"
																				iconOnly
																				circular
																				onClick={() => handleOpenDetails(event.solicitud)}
																				title="Ver productos"
																			>
																				<Icon sx={{ fontSize: 18 }}>visibility</Icon>
																			</SoftButton>
																			{canOpenRemitos && (
																				<SoftButton
																					variant="text"
																					color="dark"
																					size="small"
																					iconOnly
																					circular
																					onClick={() => onOpenRemitos?.(event.provision)}
																					title="Ver remitos"
																				>
																					<SoftBox
																						component="img"
																						src={remitoPDF}
																						alt="Ver remitos"
																						sx={{ width: 20, height: 20 }}
																					/>
																				</SoftButton>
																			)}
																		</SoftBox>
																	</SoftBox>

																	<SoftBox mb={1}>
																		<SoftTypography variant="caption" color="text" fontWeight="bold">
																			OP:{" "}
																		</SoftTypography>
																		<SoftTypography variant="caption">{event.provisionId || "-"}</SoftTypography>
																	</SoftBox>

																	<SoftBox mb={1}>
																		<SoftTypography variant="caption" color="text" fontWeight="bold">
																			Pedido:{" "}
																		</SoftTypography>
																		<SoftTypography variant="caption">
																			{event.pedidoId ? `# ${event.pedidoId} ${event.pedidoNombre}` : "-"}
																		</SoftTypography>
																	</SoftBox>

																	<SoftBox>
																		<SoftTypography variant="caption" color="text" fontWeight="bold">
																			Horario de entrega:{" "}
																		</SoftTypography>
																		<SoftTypography variant="caption" title={event.schedule.tooltip}>
																			{event.schedule.label}
																		</SoftTypography>
																	</SoftBox>
																</SoftBox>
															</SoftBox>
														))}
													</SoftBox>
												) : (
													<SoftBox mt={4} display="flex" alignItems="center" justifyContent="center">
														<SoftTypography component="span" variant="body2" color="text">
															No hay solicitudes programadas para este dia
														</SoftTypography>
													</SoftBox>
												)
											) : (
												<SoftBox mt={4} display="flex" alignItems="center" justifyContent="center">
													<SoftTypography component="span" variant="body2" color="text">
														Selecciona un dia para ver las solicitudes
													</SoftTypography>
												</SoftBox>
											)}
										</SoftBox>
									</SoftBox>
								</Card>
								<Card
									sx={{
										p: { xs: 1.5, sm: 2 },
										boxShadow: "none",
										border: "1px solid #e0e0e0",
										borderRadius: 2,
									}}
								>
									<SoftBox display="flex" flexDirection="column" gap={3}>
										<SoftBox
											display="flex"
											justifyContent="space-between"
											flexDirection={{ xs: "column", sm: "row" }}
											alignItems="left"
											flexWrap="wrap"
											gap={2}
										>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<Icon color="primary">calendar_today</Icon>
												<SoftTypography component="span" variant="h6" fontWeight="bold">
													Entrega inmediata
												</SoftTypography>
											</SoftBox>
											<SoftBox display="flex" alignItems="center" gap={1}>
												<Select value={provisionMonthFilter} onChange={handleProvisionMonthChange} size="small">
													{CALENDAR_MONTHS.map((monthName, index) => (
														<MenuItem key={monthName} value={index}>
															{monthName}
														</MenuItem>
													))}
												</Select>
												<Select value={provisionYearFilter} onChange={handleProvisionYearChange} size="small">
													{provisionYearOptions.map((yearOption) => (
														<MenuItem key={yearOption} value={yearOption}>
															{yearOption}
														</MenuItem>
													))}
												</Select>
											</SoftBox>
											<SoftButton
												color="primary"
												circular
												fullWidth
												onClick={(event) => {
													setExpandActive(true);
													const extendedMappings = {
														...mappings,
														"pendiente - entrega parcial": {
															color: "#3c4de9ff",
															icon: "schedule",
															label: ["Pendiente", "Entrega parcial"],
														},
													};
													menu?.({
														open: event.currentTarget,
														align: "right",
														options: Object.keys(extendedMappings).map((key) => ({
															name: Array.isArray(extendedMappings[key].label)
																? extendedMappings[key].label.join(" - ")
																: extendedMappings[key].label,
															icon: {
																icon: extendedMappings[key].icon,
																color: extendedMappings[key].color,
															},
															onClick: () => {
																setOPoption(
																	Array.isArray(extendedMappings[key].label)
																		? extendedMappings[key].label
																		: [extendedMappings[key].label]
																);
																setProvisionPage(1);
																setExpandActive(false);
															},
														})),
														handleClose: () => { },
														onClose: () => setExpandActive(false),
													});
												}}
											>
												{OPoption.join(" - ")}
												<Icon sx={{ rotate: expandActive ? "180deg" : "0deg", transition: "all 0.5s" }}>expand_more</Icon>
											</SoftButton>
										</SoftBox>
										<SoftBox display="flex" flexDirection="column" gap={2}>
											{filteredProvisiones.length === 0 ? (
												<SoftBox display="flex" flexDirection="column" alignItems="center" gap={1} py={3}>
													<SoftTypography component="span" variant="body2" color="text">
														No se encontraron ordenes de provision para este periodo
													</SoftTypography>
												</SoftBox>
											) : (
												filteredProvisiones.map((provision) => {
													const statusMeta = getProvisionStatusMeta(provision?.estado);
													const dueDate = provision?.fechaVencimiento ? dayjs(provision.fechaVencimiento) : null;
													const formattedDueDate = dueDate && dueDate.isValid() ? dueDate.format("DD/MM/YYYY") : "-";
													const pedidoId = provision?.pedidoCompra?.id;
													const pedidoNombre = provision?.pedidoCompra?.nombre;
													const pedidoLabel = pedidoId ? `#${pedidoId} ${pedidoNombre || ""}`.trim() : "Sin pedido";
													const { message: deadlineMessage, color: deadlineColor } = getProvisionDeadlineInfo(
														dueDate,
														statusMeta.label,
														today
													);
													const matchedSolicitud = solicitudByProvisionId.get(String(provision.id));
													const provisionSolicitud = selectLatestSolicitudFromProvision(provision);
													const fallbackSolicitud = buildSolicitudFromProvision(provision);
													const normalizedProvisionSolicitud = provisionSolicitud
														? {
															...provisionSolicitud,
															detalles:
																Array.isArray(provisionSolicitud.detalles) && provisionSolicitud.detalles.length
																	? provisionSolicitud.detalles
																	: fallbackSolicitud?.detalles || [],
														}
														: null;
													const detailsPayload = matchedSolicitud || normalizedProvisionSolicitud || fallbackSolicitud;

													return (
														<Card
															key={provision.id}
															sx={{
																boxShadow: "none",
																border: "1px solid #e0e0e0",
																borderRadius: 2,
															}}
														>
															<SoftBox p={2} display="flex" flexDirection="column" gap={1.5}>
																<SoftBox
																	display="flex"
																	justifyContent="space-between"
																	alignItems={{ xs: "flex-start", sm: "center" }}
																	flexDirection={{ xs: "column", sm: "row" }}
																	gap={1}
																>
																	<SoftTypography
																		variant="button"
																		color="dark"
																		fontWeight="bold"
																		sx={{
																			cursor: "pointer",
																			"&:hover": { textDecoration: "underline" },
																		}}
																		onClick={() => window.open(`/op/pdf/${provision.id ?? 0}`)}
																	>
																		OP #{provision.id}
																	</SoftTypography>
																	<SoftBox display="flex" alignItems="center" gap={1}>
																		<Icon sx={{ color: statusMeta.color }}>{statusMeta.icon}</Icon>
																		<SoftTypography variant="button" fontWeight="bold">
																			{statusMeta.label}
																		</SoftTypography>
																	</SoftBox>
																</SoftBox>
																<SoftBox
																	display="flex"
																	justifyContent="space-between"
																	alignItems={{ xs: "flex-start", sm: "center" }}
																	gap={2}
																	flexWrap="wrap"
																>
																	<SoftBox gap={1} display="flex" alignItems="center">
																		<SoftTypography variant="caption" color="text" fontWeight="bold">
																			Pedido
																		</SoftTypography>
																		<SoftTypography variant="caption" color="text">
																			{pedidoLabel}
																		</SoftTypography>
																	</SoftBox>
																	<SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
																		<SoftTypography variant="caption" color="text" fontWeight="bold">
																			Fecha de vencimiento:
																		</SoftTypography>
																		<SoftTypography variant="caption" color="text">
																			{formattedDueDate}
																		</SoftTypography>
																		{provision?.motivoProrroga && (
																			<Tooltip
																				title={
																					<SoftBox display="flex" flexDirection="column" gap={0.5}>
																						<SoftTypography variant="caption" color="warning" fontWeight="bold">
																							Motivo de la prorroga
																						</SoftTypography>
																						<SoftTypography variant="caption">
																							{provision.motivoProrroga}
																						</SoftTypography>
																					</SoftBox>
																				}
																			>
																				<Icon sx={{ color: "#78909c", cursor: "pointer" }}>help_outline</Icon>
																			</Tooltip>
																		)}
																		<SoftTypography variant="caption" mt={1} color={deadlineColor}>
																			{deadlineMessage}
																		</SoftTypography>
																	</SoftBox>
																	<SoftBox display="flex" flexDirection="column" gap={1.5}>
																		<SoftTypography
																			variant="caption"
																			fontWeight="regular"
																			color="info"
																			onClick={() => onOpenRemitos?.(provision)}
																			sx={{ textDecorationLine: "underline", cursor: "pointer" }}
																		>
																			Remitos
																		</SoftTypography>
																	</SoftBox>
																	<SoftBox display="flex" flexDirection="column" gap={1.5}>
																		<SoftTypography
																			variant="caption"
																			fontWeight="regular"
																			color="info"
																			onClick={() => detailsPayload && handleOpenDetails(detailsPayload)}
																			sx={{ textDecorationLine: "underline", cursor: "pointer" }}
																		>
																			Detalles
																		</SoftTypography>
																	</SoftBox>
																</SoftBox>
															</SoftBox>
														</Card>
													);
												})
											)}
											<SoftBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
												<SoftTypography variant="caption" color="text">
													Pagina {provisionPage}
												</SoftTypography>
												<SoftBox display="flex" gap={1} flexWrap="wrap">
													<SoftButton
														variant="outlined"
														color="primary"
														size="small"
														disabled={!canGoPreviousProvisionPage || loadingProvisiones}
														onClick={goToPreviousProvisionPage}
													>
														<Icon sx={{ fontSize: 18, mr: 0.5 }}>navigate_before</Icon>
														Anterior
													</SoftButton>
													<SoftButton
														variant="outlined"
														color="primary"
														size="small"
														disabled={!canGoNextProvisionPage || loadingProvisiones}
														onClick={goToNextProvisionPage}
													>
														Siguiente
														<Icon sx={{ fontSize: 18, ml: 0.5 }}>navigate_next</Icon>
													</SoftButton>
												</SoftBox>
											</SoftBox>
										</SoftBox>
									</SoftBox>
								</Card>
							</SoftBox>
						)}
					</SoftBox>
				</Collapse>
			</Card>
			<Modal open={detailsModal.open} onClose={handleCloseDetails} closeAfterTransition>
				<Fade in={detailsModal.open}>
					<Card
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							width: { xs: "90%", sm: "80%", lg: "60%" },
							maxHeight: "80vh",
							overflow: "hidden",
							p: { xs: 2, sm: 3 },
						}}
					>
						<SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
							<SoftTypography variant="h6" fontWeight="bold">
								Detalle de productos
							</SoftTypography>
							<SoftButton variant="text" color="dark" iconOnly circular aria-label="Cerrar detalle de productos" onClick={handleCloseDetails}>
								<Icon>close</Icon>
							</SoftButton>
						</SoftBox>
						{detalleSolicitud ? (
							<>
								<SoftTypography variant="body2" color="text" mb={2}>
									{`Proveedor: ${getProveedorName(detalleSolicitud.proveedor) || "-"}`}
								</SoftTypography>
								<SoftBox
									display={{ xs: "none", lg: "grid" }}
									gridTemplateColumns="minmax(200px, 2fr) 140px 120px 140px 160px 140px"
									px={2}
									py={1}
									sx={{ backgroundColor: "#f0f4f8", borderRadius: 1 }}
								>
									<SoftTypography variant="caption" fontWeight="bold">
										Nombre
									</SoftTypography>
									<SoftTypography variant="caption" fontWeight="bold">
										Cantidad solicitada
									</SoftTypography>
									<SoftTypography variant="caption" fontWeight="bold">
										Unidad
									</SoftTypography>
									<SoftTypography variant="caption" fontWeight="bold">
										Total provision
									</SoftTypography>
									<SoftTypography variant="caption" fontWeight="bold">
										Cantidad entregada
									</SoftTypography>
									<SoftTypography variant="caption" fontWeight="bold" textAlign="center">
										Restante
									</SoftTypography>
								</SoftBox>
								<SoftBox mt={1} sx={{ maxHeight: "45vh", overflowY: "auto", overflowX: "hidden" }}>
									{detalleRows.length ? (
										detalleRows.map((row, index) => {
											const faltanteLabel = row.completa
												? "Completa"
												: `${formatNumberValue(row.faltante)}${row.unidad ? ` ${row.unidad}` : ""}`;
											return (
												<Fragment key={`${row.nombre}-${index}`}>
													<SoftBox
														display={{ xs: "none", lg: "grid" }}
														gridTemplateColumns="minmax(200px, 2fr) 140px 120px 140px 160px 140px"
														px={2}
														py={1.25}
														sx={{ borderBottom: "1px solid #eceff1", alignItems: "center" }}
													>
														<SoftTypography variant="caption">{row.nombre}</SoftTypography>
														<SoftTypography variant="caption">{formatNumberValue(row.cantidadSolicitada)}</SoftTypography>
														<SoftTypography variant="caption">{row.unidad}</SoftTypography>
														<SoftTypography variant="caption">{formatNumberValue(row.totalProvision)}</SoftTypography>
														<SoftTypography variant="caption">{formatNumberValue(row.cantidadEntregada)}</SoftTypography>
														<SoftBox display="flex" justifyContent="center">
															<SoftBadge
																badgeContent={faltanteLabel}
																variant="color"
																color={row.completa ? "#2196f3" : "#f44336"}
															/>
														</SoftBox>
													</SoftBox>
													<SoftBox
														component="article"
														display={{ xs: "block", lg: "none" }}
														p={2}
														mb={1.5}
														sx={{ border: "1px solid #e9ecef", borderRadius: 2 }}
													>
														<SoftTypography variant="button" color="dark" fontWeight="bold" sx={{ overflowWrap: "anywhere" }}>
															{row.nombre}
														</SoftTypography>
														<SoftBox display="grid" gridTemplateColumns="1fr 1fr" gap={1.5} mt={1.5}>
															<SoftBox>
																<SoftTypography variant="caption" color="dark" fontWeight="bold">
																	Solicitada
																</SoftTypography>
																<SoftTypography variant="caption" color="dark" display="block">
																	{formatNumberValue(row.cantidadSolicitada)} {row.unidad}
																</SoftTypography>
															</SoftBox>
															<SoftBox>
																<SoftTypography variant="caption" color="dark" fontWeight="bold">
																	Total provisión
																</SoftTypography>
																<SoftTypography variant="caption" color="dark" display="block">
																	{formatNumberValue(row.totalProvision)} {row.unidad}
																</SoftTypography>
															</SoftBox>
															<SoftBox>
																<SoftTypography variant="caption" color="dark" fontWeight="bold">
																	Entregada
																</SoftTypography>
																<SoftTypography variant="caption" color="dark" display="block">
																	{formatNumberValue(row.cantidadEntregada)} {row.unidad}
																</SoftTypography>
															</SoftBox>
															<SoftBox>
																<SoftTypography variant="caption" color="dark" fontWeight="bold">
																	Restante
																</SoftTypography>
																<SoftBadge
																	badgeContent={faltanteLabel}
																	variant="color"
																	color={row.completa ? "#1769aa" : "#b71c1c"}
																/>
															</SoftBox>
														</SoftBox>
													</SoftBox>
												</Fragment>
											);
										})
									) : (
										<SoftBox py={4} display="flex" justifyContent="center">
											<SoftTypography variant="body2" color="text">
												No hay informacion disponible.
											</SoftTypography>
										</SoftBox>
									)}
								</SoftBox>
							</>
						) : (
							<SoftTypography variant="body2" color="text">
								No hay informacion disponible.
							</SoftTypography>
						)}
					</Card>
				</Fade>
			</Modal>
		</>
	);
}

export default CalendarioEntregas;
